import { useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormHelperText,
  SxProps,
  Theme,
  MenuItem,
  Select,
  Chip,
  Tooltip
} from '@mui/material';
import { AccessTime as TimeIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { TimeRange } from '../../types';
import { isRangeSlotPerfectFit, WrapPartners } from '../../utils';

// Helper to parse 24h time to 12h components
const parseTime = (time24: string): { hour: number; minute: number; isPM: boolean } => {
  const [h, m] = time24.split(':').map(Number);
  const isPM = h >= 12;
  const displayHour = h % 12 || 12;
  return { hour: displayHour, minute: m, isPM };
};

const getTimeLabel = (time24: string, isEndTime: boolean): string => {
  if (isEndTime && time24 === '23:59') {
    return '12:00 AM';
  }

  const { hour, minute, isPM } = parseTime(time24);
  return `${hour}:${String(minute).padStart(2, '0')} ${isPM ? 'PM' : 'AM'}`;
};

const getMinutesFromTime = (time24: string, isEndTime: boolean): number => {
  if (isEndTime && time24 === '23:59') {
    return 24 * 60;
  }

  const [h, m] = time24.split(':').map(Number);
  return h * 60 + m;
};

// Check if an end time results in perfect fit
export const isPerfectFit = (endTime: string, startTime: string, duration: number): boolean => {
  const startTotal = getMinutesFromTime(startTime, false);
  const endTotal = getMinutesFromTime(endTime, true);
  const diff = endTotal - startTotal;
  return diff > 0 && diff % duration === 0;
};

interface SlotTimePickerProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  duration: number;
  isEndTime?: boolean;
  startTime?: string;
  error?: boolean;
  helperText?: string | null;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  size?: 'small' | 'medium';
  showPerfectFit?: boolean;
  showPerfectFitIndicator?: boolean;
  disabled?: boolean;
  allowMidnightWrap?: boolean;
  rangeIndex?: number;
  ranges?: TimeRange[];
  wrapPartners?: WrapPartners;
}

export const SlotTimePicker = ({
  label,
  value,
  onChange,
  duration,
  isEndTime = false,
  startTime,
  error,
  helperText,
  fullWidth,
  sx,
  size,
  showPerfectFit = false,
  showPerfectFitIndicator = true,
  disabled = false,
  allowMidnightWrap = false,
  rangeIndex,
  ranges,
  wrapPartners,
}: SlotTimePickerProps) => {
  const hasValue = value !== '';

  const getOptionPerfectFit = (endTime: string): boolean => {
    if (!isEndTime || !startTime) return false;

    if (
      ranges &&
      rangeIndex !== undefined &&
      ranges[rangeIndex]
    ) {
      const optionRanges = ranges.map((range, index) => (
        index === rangeIndex
          ? { ...range, start_time: startTime, end_time: endTime }
          : range
      ));

      return isRangeSlotPerfectFit(optionRanges[rangeIndex], duration, wrapPartners, optionRanges);
    }

    return isPerfectFit(endTime, startTime, duration);
  };

  // Generate all valid time options
  const timeOptions = useMemo(() => {
    const options: { value: string; label: string; isPerfectFit: boolean }[] = [];
    
    // Generate times in 15-min increments throughout the day
    for (let h = 0; h < 24; h++) {
      for (let m of [0, 15, 30, 45]) {
        const timeValue = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        const label = getTimeLabel(timeValue, isEndTime);

        const fit = getOptionPerfectFit(timeValue);

        options.push({ value: timeValue, label, isPerfectFit: fit });
      }
    }

    if (isEndTime) {
      options.push({ value: '23:59', label: getTimeLabel('23:59', true), isPerfectFit: getOptionPerfectFit('23:59') });
    }

    return options;
  }, [duration, isEndTime, startTime, ranges, rangeIndex, wrapPartners]);

  // Filter options based on start time for end time picker
  const filteredOptions = useMemo(() => {
    if (!isEndTime || !startTime) return timeOptions;
    
    const startTotal = getMinutesFromTime(startTime, false);
    const minimumEndTotal = startTotal + duration;
    
    return timeOptions.filter(opt => {
      const endTotal = getMinutesFromTime(opt.value, true);
      // Allow a short range ending exactly at midnight (the midnight-wrap tail)…
      if (allowMidnightWrap && opt.value === '23:59' && endTotal > startTotal) return true;
      // …and a short range starting at midnight (the midnight-wrap head).
      if (allowMidnightWrap && startTime === '00:00' && endTotal > startTotal) return true;
      return endTotal >= minimumEndTotal;
    });
  }, [duration, timeOptions, isEndTime, startTime, allowMidnightWrap]);

  const currentIsPerfectFit = isEndTime && startTime
    ? getOptionPerfectFit(value)
    : false;

  const displayValue = hasValue ? getTimeLabel(value, isEndTime) : 'Select time';

  return (
    <FormControl fullWidth={fullWidth} error={error} sx={sx} size={size}>
      {label && (
        <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ mb: 0.5, ml: 1.5 }}>
          {label}
        </Typography>
      )}
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size={size}
          error={error}
          disabled={disabled}
          displayEmpty
          renderValue={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon fontSize="small" color="action" />
              <Typography sx={{ fontWeight: 500, color: hasValue ? 'text.primary' : 'text.secondary' }}>
                {displayValue}
              </Typography>
            </Box>
          )}
          sx={{
            minWidth: size === 'small' ? 140 : 160,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pr: 2,
              py: size === 'small' ? 0.8 : 1.5,
            },
          }}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            PaperProps: {
              sx: {
                maxHeight: 300,
                zIndex: (theme: Theme) => theme.zIndex.modal + 100,
                '& .MuiMenuItem-root': {
                  fontSize: '0.9rem',
                },
              },
            },
            PopoverClasses: {
              root: 'slot-time-picker-menu',
            },
          }}
        >
          <MenuItem value="">
            <em>Select time</em>
          </MenuItem>
          {filteredOptions.map((option) => (
            <MenuItem 
              key={option.value} 
              value={option.value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                minWidth: 140,
              }}
            >
              <span>{option.label}</span>
              {showPerfectFit && isEndTime && option.isPerfectFit && (
                <Tooltip title="Perfect fit - no remainder time">
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label="Fit"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ 
                      height: 20, 
                      fontSize: '0.7rem',
                      '& .MuiChip-icon': { color: 'success.main' }
                    }}
                  />
                </Tooltip>
              )}
            </MenuItem>
          ))}
        </Select>
        
        {showPerfectFit && showPerfectFitIndicator && isEndTime && (
          <Tooltip title={currentIsPerfectFit ? "Perfect fit - all time is bookable" : "Adjust to eliminate remainder time"}>
            <Chip
              size="small"
              color={currentIsPerfectFit ? 'success' : 'warning'}
              variant="outlined"
              label={currentIsPerfectFit ? '✓ Fit' : 'Adjust'}
              sx={{ height: 24, fontSize: '0.75rem', marginLeft: 1 }}
            />
          </Tooltip>
        )}
      </Box>
      
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
